import {
    addDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    query,
    updateDoc,
    where,
    startAfter,
    limit,
    getCountFromServer,
    QueryDocumentSnapshot,
    QueryFieldFilterConstraint,
    Timestamp
} from 'firebase/firestore';
import {
    Customer,
    customersRef,
    normalizePhone,
    PointTransaction,
    PointTransactionSource,
    PointTransactionType
} from '@api/Customers/Customer';
import { showAlertWarning } from '@utils/Alerts/Message';
import RewardServ from '@api/Rewards/RewardServ';
import OrderServ from '@api/Orders/OrderServ';
import { Order } from '@api/Orders/Order';
import UserServ from '@api/Users/UserServ';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeChanges } from '@api/ActivityLogs/DescribeChanges';
import {
    CUSTOMER_FIELDS,
    CUSTOMER_PROFILE_FIELDS,
    describeDeletedPointTransaction,
    describeManualPoints
} from '@api/ActivityLogs/CustomerActivity';
import { syncCustomerToCrm } from '@api/Customers/CrmSyncClient';
import { rebuildWallet } from '@api/Customers/PointsRebuild';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import store from '@store/index';

export interface NewCustomerData {
    uid?: string;
    firstName: string;
    lastName?: string;
    phone: string;
    email?: string;
    birthdayDay?: number;
    birthdayMonth?: number;
    birthdayYear?: number;
    branchOfficeName?: string;
    branchOfficeId?: string;
}

// ID del custom field "Sucursal" creado en ColegaStudio (Settings → Custom Fields).
// Reemplaza este valor por el ID real que te da el CRM.
const COLEGA_SUCURSAL_FIELD_ID = 'contact.sucursal_preferida';

export interface PaginatedQueryParams {
    search: string;
    pageSize?: number;
    lastDoc?: QueryDocumentSnapshot;
    startDate?: Date;
    endDate?: Date;
}

export default {
    async saveCustomer(newCustomerData: NewCustomerData): Promise<Customer> {
        const emailValue =
            newCustomerData.email && newCustomerData.email.trim() !== '' ? newCustomerData.email : undefined;
        if (!newCustomerData.uid) {
            // El teléfono se guarda NORMALIZADO: es la llave de búsqueda del POS y
            // de login de la web. Antes se guardaba tal cual se tecleara ("221 364
            // 5546", "+52...", incluso nombres) y ese cliente quedaba invisible —
            // el POS lo volvía a crear y nacían duplicados con carteras separadas.
            const normalizedPhone = normalizePhone(newCustomerData.phone);
            if (normalizedPhone.length !== 10) {
                throw new Error('Captura un teléfono de 10 dígitos');
            }
            newCustomerData.phone = normalizedPhone;
            // Mismo patrón que `CashRegisterServ`: lo explícito manda y, si no
            // viene, la sucursal en operación del sidebar.
            const joinedFromBranchId =
                newCustomerData.branchOfficeId ?? store.getState().auth.branchOfficeSelected?.id;
            const customerExist = await this.getCustomerByPhone(normalizedPhone);
            if (customerExist) {
                throw new Error('El telefono ya se encuentra registrado');
            }
            try {
                await axiosPrivate.post('/colegastudio/contacts', {
                    locationId: 'IjcptuxHblU49ONu595G',
                    firstName: newCustomerData.firstName,
                    lastName: newCustomerData.lastName || '',
                    email: emailValue,
                    phone: newCustomerData.phone,
                    tags: ['pos_krispis'],
                    ...(newCustomerData.branchOfficeName
                        ? {
                              customFields: [
                                  {
                                      id: COLEGA_SUCURSAL_FIELD_ID,
                                      field_value: newCustomerData.branchOfficeName
                                  },
                                  // Sucursal que REGISTRÓ al cliente (Irving,
                                  // 2026-08-31) — solo altas nuevas; el backfill
                                  // histórico (derivarla del PRIMER pedido) va
                                  // junto al rebuild global de puntos.
                                  {
                                      id: 'contact.sucursal_de_registro',
                                      field_value: newCustomerData.branchOfficeName
                                  }
                              ]
                          }
                        : {})
                });
            } catch (crmError: any) {
                // ⚠️ El CRM NUNCA bloquea la creación del cliente (2026-08-19, caso
                // Fanny Acosta): "duplicated contacts" significa que el contacto YA
                // vive en el CRM — que es justo el estado deseado — y aun así
                // abortaba el alta local, dejando al cliente sin existir en el POS.
                // Y un CRM caído tampoco puede impedir una venta: el registro en
                // ColegaStudio es marketing, no operación.
                console.error('No se pudo crear el contacto en el CRM:', crmError);
                const serverMessage = String(crmError.response?.data?.details?.message || '');
                if (!/duplicated contacts/i.test(serverMessage)) {
                    showAlertWarning(
                        'El cliente se creará, pero no se pudo registrar en el CRM' +
                            (serverMessage ? `: ${serverMessage}` : '')
                    );
                }
            }
            const rewards = await RewardServ.getRewards();
            const points = rewards?.newUser?.status ? rewards.newUser.points : 0;
            const createdAt = Timestamp.now();
            // Primer registro del historial: puntos de bienvenida por nuevo usuario.
            const welcomeTransactions: PointTransaction[] =
                points > 0
                    ? [
                          {
                              date: createdAt,
                              points,
                              type: PointTransactionType.Initial,
                              source: PointTransactionSource.System,
                              description: `Se otorgaron ${points} puntos de bienvenida`
                          }
                      ]
                    : [];
            const newCustomer: Customer = {
                createdAt,
                profile: {
                    status: true,
                    firstName: newCustomerData.firstName,
                    lastName: newCustomerData.lastName,
                    name: `${newCustomerData.firstName} ${newCustomerData.lastName}`,
                    addresses: [],
                    wallet: {
                        currentPoints: points,
                        transactions: welcomeTransactions
                    },
                    birthdayDay: newCustomerData.birthdayDay,
                    birthdayMonth: newCustomerData.birthdayMonth,
                    birthdayYear: newCustomerData.birthdayYear
                },
                email: emailValue,
                phone: newCustomerData.phone,
                // La sucursal que dio de alta: el sync del CRM la resuelve a
                // nombre para el campo "Sucursal de registro".
                //
                // 🚨 El fallback a la sucursal EN OPERACIÓN no es adorno: el
                // alta del POS (`ModalGetCustomer`, por donde entra casi todo
                // el mundo) mandaba `branchOfficeName` pero NO el id, así que
                // el campo existía en el código y **0 de 15,308 clientes lo
                // tenían** (medido el 18-sep). El dato se resuelve aquí, en la
                // función que escribe, y no en cada modal — si no, el próximo
                // camino de alta vuelve a nacer sin sucursal.
                ...(joinedFromBranchId ? { joinedFromBranchId } : {})
            };
            const data = await addDoc(customersRef, newCustomer);
            newCustomer.uid = data.id;
            await updateDoc(doc(customersRef, newCustomer.uid), { ...newCustomer });
            // CRM: el create de arriba no lleva puntos — el sync completa el
            // bono de bienvenida y deja la marca. Fire-and-forget.
            syncCustomerToCrm(newCustomer.uid);
            return newCustomer;
        }
        const customer = await this.getCustomer(newCustomerData.uid!);
        const perfilNuevo = {
            ...customer.profile,
            firstName: newCustomerData.firstName,
            lastName: newCustomerData.lastName,
            name: `${newCustomerData.firstName} ${newCustomerData.lastName}`,
            birthdayDay: newCustomerData.birthdayDay,
            birthdayMonth: newCustomerData.birthdayMonth,
            birthdayYear: newCustomerData.birthdayYear
        };
        await updateDoc(doc(customersRef, customer.uid), {
            ...customer,
            email: emailValue,
            profile: perfilNuevo
        });
        // Qué cambió, no solo que se tocó. **Si no cambió nada, no se escribe**:
        // abrir el modal y guardar sin editar no es un evento.
        // ⚠️ Este camino NO toca el teléfono (solo se fija al crear), así que lo
        // que queda registrado es nombre y correo. El día que algo mueva el
        // teléfono —la IDENTIDAD del cliente— los specs ya lo contemplan.
        try {
            const frase = [
                describeChanges(customer, { ...customer, email: emailValue }, CUSTOMER_FIELDS),
                describeChanges(customer.profile, perfilNuevo, CUSTOMER_PROFILE_FIELDS)
            ]
                .filter((texto) => texto !== '')
                .join(' y ');
            if (frase && customer.uid) {
                await ActivityLogServ.addActivityLog('customer', customer.uid, frase);
            }
        } catch (error) {
            console.error('No se pudo registrar la edición del cliente', error);
        }
        // CRM con `force`: aquí cambiaron DATOS, no puntos, y `needsCrmSync`
        // solo mira los puntos — sin esto el CRM se quedaba con el nombre viejo.
        syncCustomerToCrm(customer.uid, { force: true });
        return customer;
    },
    async getCustomers(branchOfficeId?: string): Promise<Customer[]> {
        const customers: Customer[] = [];
        let queryDoc = query(customersRef);
        if (branchOfficeId && branchOfficeId !== 'all') {
            queryDoc = query(customersRef, where('branchOfficeId', '==', branchOfficeId));
        }
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return customers;
        }
        snapshot.forEach((doc: any) => {
            customers.push(doc.data() as Customer);
        });
        return customers;
    },
    async getCustomersPaginated({
        search,
        pageSize = 10,
        lastDoc,
        startDate,
        endDate
    }: PaginatedQueryParams): Promise<{ data: Customer[]; count: number; lastVisible?: QueryDocumentSnapshot }> {
        const filtersQuery: QueryFieldFilterConstraint[] = [];

        // Firebase no permite filtros de desigualdad (>=, <=) en diferentes campos.
        // Si hay búsqueda de texto, solo usar filtros de texto (ignorar fecha).
        // Si no hay búsqueda, usar filtros de fecha.
        const hasSearch = search !== '';

        if (hasSearch) {
            const numericSearch = search.replace(/\D/g, '');
            if (numericSearch.length > 0) {
                filtersQuery.push(where('phone', '>=', numericSearch));
                filtersQuery.push(where('phone', '<=', numericSearch + '\uf8ff'));
            } else {
                filtersQuery.push(where('profile.name', '>=', search));
                filtersQuery.push(where('profile.name', '<=', search + '\uf8ff'));
            }
        } else if (startDate) {
            filtersQuery.push(where('createdAt', '>=', Timestamp.fromDate(startDate)));
        }
        if (!hasSearch && endDate) {
            filtersQuery.push(where('createdAt', '<=', Timestamp.fromDate(endDate)));
        }
        const qCount = query(customersRef, ...filtersQuery);
        const q = query(customersRef, ...filtersQuery, ...(lastDoc ? [startAfter(lastDoc)] : []), limit(pageSize));
        const snapshot = await getDocs(q);
        const snapshotCount = await getCountFromServer(qCount);
        return {
            data: snapshot.docs.map((doc) => doc.data() as Customer),
            count: snapshotCount.data().count,
            lastVisible: snapshot.docs[snapshot.docs.length - 1]
        };
    },
    async getCustomer(customerId: string): Promise<Customer> {
        const userDocument = await getDoc(doc(customersRef, customerId));
        return userDocument.data() as Customer;
    },
    async getCustomerByPhone(phone?: string): Promise<Customer | undefined> {
        let customer = undefined;
        // La llave es el teléfono NORMALIZADO (10 dígitos): capturar con espacios
        // o +52 no debe volver invisible a un cliente que sí existe
        const normalized = normalizePhone(phone);
        if (!normalized) {
            return customer;
        }
        const queryCustomer = query(customersRef, where('phone', '==', normalized));
        const snapshot = await getDocs(queryCustomer);
        snapshot.forEach((doc) => {
            customer = doc.data() as Customer;
        });
        return customer;
    },
    /**
     * Reconstruye la cartera de puntos de los clientes desde sus órdenes REALES,
     * conservando los puntos que no provienen de órdenes, y corrige los gastos en los
     * que el sistema permitió usar puntos que el cliente no tenía.
     *
     * Cómo funciona (por cliente):
     *  1. Conserva las transacciones que NO vienen de órdenes y que no son ajustes
     *     automáticos previos: puntos de bienvenida (Initial), manuales, QR, promos,
     *     gift cards, etc. (no se pueden reconstruir desde órdenes).
     *  2. Descarta TODAS las transacciones de órdenes y las reconstruye desde las
     *     órdenes reales que existen hoy (pagadas o en curso: Paid, Preparing,
     *     Ready, Route, Completed). El CANJE se descuenta desde que la orden está
     *     pagada/en curso; la GANANCIA se otorga solo al completarse (igual que el
     *     flujo vivo). Así se eliminan automáticamente las transacciones de
     *     órdenes borradas, canceladas o con datos corruptos.
     *  3. Mezcla todo y lo ordena cronológicamente (en empate de fecha, los créditos
     *     se aplican antes que los canjes).
     *  4. Reproduce el historial llevando un saldo. Cuando un canje dejaría el saldo en
     *     negativo (el sistema dejó gastar puntos que no había), inserta JUSTO EN ESE
     *     MOMENTO un ajuste por el faltante: el saldo queda en 0 ahí, y las compras
     *     posteriores siguen sumando normalmente. Una orden que requirió ajuste NO
     *     otorga puntos de ganancia (no se premia la compra pagada con puntos inexistentes).
     *
     * Propiedades:
     *  - Reconstruye desde la fuente de verdad (órdenes), tolerando historial corrupto.
     *  - El error pasado se "perdona" en el punto exacto donde ocurrió; lo ganado
     *    después se conserva (no se descuenta).
     *  - Idempotente: correrlo varias veces produce el mismo resultado.
     *  - Auto-reparable: limpia los ajustes erróneos que generó la versión anterior.
     */
    async correctUsersPointsBasedOnOrders(customers: Customer[]): Promise<void> {
        const validCustomers = customers.filter((user) => user.uid && user.profile?.wallet);

        const reward = await RewardServ.getRewards();
        if (!reward) {
            return;
        }

        const batchSize = 100;

        for (let i = 0; i < validCustomers.length; i += batchSize) {
            const batch = validCustomers.slice(i, i + batchSize);
            await Promise.all(
                batch.map(async (customer) => {
                    const customerId = customer.uid!;
                    try {
                        // `getOrdersByUserId` devuelve las órdenes que EXISTEN hoy,
                        // así que las borradas y canceladas no entran — y por eso el
                        // rebuild repara solo la cartera de quien tuvo una purgada.
                        const orders = await OrderServ.getOrdersByUserId(customerId);
                        const { currentPoints, transactions } = rebuildWallet({
                            createdAt: customer.createdAt || Timestamp.now(),
                            transactions: customer.profile!.wallet!.transactions || [],
                            orders,
                            reward
                        });
                        await updateDoc(doc(customersRef, customerId), {
                            'profile.wallet.currentPoints': currentPoints,
                            'profile.wallet.transactions': transactions
                        });
                        // CRM: el saldo acaba de reconstruirse (paridad admin↔web).
                        syncCustomerToCrm(customerId);
                    } catch (error) {
                        console.error(`❌ Error al corregir puntos para cliente ${customerId}:`, error);
                    }
                })
            );

            await new Promise((resolve) => setTimeout(resolve, 500));
        }
    },

    async addPoints(customerId: string, addPoints: number): Promise<Customer | undefined> {
        const customer = await this.getCustomer(customerId);
        if (!customer || !customer.profile?.wallet) return;
        const currentPoints = (customer.profile.wallet.currentPoints || 0) + addPoints;
        const transactions = customer.profile.wallet.transactions || [];
        const createdBy = await UserServ.getUser();
        transactions.push({
            date: Timestamp.now(),
            points: addPoints,
            type: PointTransactionType.Earned,
            source: PointTransactionSource.Manual,
            description: `Se otorgaron ${addPoints.toFixed(2)} puntos`,
            createdBy
        });
        await updateDoc(doc(customersRef, customerId), {
            'profile.wallet': {
                currentPoints,
                transactions
            }
        });
        // El movimiento ya queda en la cartera con su `createdBy` (D6), pero el
        // renglón se escribe igual: es la ÚNICA forma de ver los ajustes manuales
        // juntos, y es justo lo que se busca cuando un saldo no cuadra. Son unos
        // pocos al día.
        try {
            await ActivityLogServ.addActivityLog(
                'customer',
                customerId,
                describeManualPoints(addPoints, currentPoints)
            );
        } catch (error) {
            console.error('No se pudo registrar el ajuste manual de puntos', error);
        }
        syncCustomerToCrm(customerId);
        return customer;
    },
    async orderAddPoints(customerId: string, order: Order, pointsEarned: number): Promise<void> {
        if (!(pointsEarned > 0)) return;
        const customer = await this.getCustomer(customerId);
        if (!customer || !customer.profile?.wallet) return;

        const transactions = customer.profile.wallet.transactions || [];
        // Idempotente: si esta orden ya otorgó puntos (recálculo previo, doble
        // ejecución de completeOrder), no volver a abonarlos.
        const alreadyEarned =
            !!order.id &&
            transactions.some(
                (tx) =>
                    tx.referenceId === order.id &&
                    tx.source === PointTransactionSource.Order &&
                    tx.type === PointTransactionType.Earned
            );
        if (alreadyEarned) return;

        const currentPoints = (customer.profile.wallet.currentPoints || 0) + pointsEarned;

        transactions.push({
            date: order.date || Timestamp.now(),
            points: pointsEarned,
            type: PointTransactionType.Earned,
            source: PointTransactionSource.Order,
            description: `Ganó ${pointsEarned.toFixed(2)} puntos por compra en orden ${order.shortId}`,
            referenceId: order.id
        });

        await updateDoc(doc(customersRef, customerId), {
            'profile.wallet': {
                currentPoints,
                transactions
            }
        });
        syncCustomerToCrm(customerId);
    },
    async orderRemovePoints(customerId: string, order: Order, removePoints: number): Promise<void> {
        if (!(removePoints > 0)) return;
        const customer = await this.getCustomer(customerId);
        if (!customer || !customer.profile?.wallet) return;
        const transactions = customer.profile.wallet.transactions || [];
        // Idempotente: si el canje de esta orden ya está registrado (al pagar por
        // webhook/POS, al completar, o por el recálculo), no descontarlo dos veces.
        const alreadySpent =
            !!order.id &&
            transactions.some(
                (tx) =>
                    tx.referenceId === order.id &&
                    tx.source === PointTransactionSource.Order &&
                    tx.type === PointTransactionType.Spent
            );
        if (alreadySpent) return;

        const currentPoints = (customer.profile.wallet.currentPoints || 0) - removePoints;

        transactions.push({
            date: order.date || Timestamp.now(),
            points: removePoints,
            type: PointTransactionType.Spent,
            source: PointTransactionSource.Order,
            description: `Canje de ${removePoints} puntos en orden ${order.shortId}`,
            referenceId: order.id
        });

        await updateDoc(doc(customersRef, customerId), {
            'profile.wallet': {
                currentPoints,
                transactions
            }
        });
        syncCustomerToCrm(customerId);
    },
    async deleteTransaction(customerId: string, transactionToDelete: PointTransaction): Promise<void> {
        const customerData = await this.getCustomer(customerId);
        if (!customerData.profile?.wallet?.transactions) {
            throw new Error('El usuario no tiene historial de transacciones');
        }

        const transactions = [...customerData.profile?.wallet.transactions];

        // Buscar la transacción por sus propiedades únicas
        const transactionIndex = transactions.findIndex(
            (tx) =>
                tx.date.seconds === transactionToDelete.date.seconds &&
                tx.points === transactionToDelete.points &&
                tx.type === transactionToDelete.type &&
                tx.description === transactionToDelete.description &&
                tx.source === transactionToDelete.source
        );

        if (transactionIndex === -1) {
            throw new Error('No se pudo encontrar la transacción');
        }

        transactions.splice(transactionIndex, 1);

        let newCurrentPoints = customerData.profile.wallet.currentPoints;
        if (
            transactionToDelete.type === PointTransactionType.Earned ||
            transactionToDelete.type === PointTransactionType.Initial
        ) {
            newCurrentPoints -= transactionToDelete.points;
        } else if (transactionToDelete.type === PointTransactionType.Spent) {
            newCurrentPoints += transactionToDelete.points;
        }

        await updateDoc(doc(customersRef, customerId), {
            'profile.wallet.transactions': transactions,
            'profile.wallet.currentPoints': newCurrentPoints
        });
        // ⚠️ **El único borrado del sistema que destruye evidencia**: un ajuste
        // manual guarda dentro su `createdBy`, y al quitar el movimiento se va
        // con él. Por eso el renglón lleva TODO dentro del texto (M4, 2026-09-11).
        // Nunca lanza: la bitácora observa, no gobierna.
        try {
            await ActivityLogServ.addActivityLog(
                'customer',
                customerId,
                describeDeletedPointTransaction(transactionToDelete)
            );
        } catch (error) {
            console.error('No se pudo registrar el borrado del movimiento de puntos', error);
        }
        syncCustomerToCrm(customerId);
    },
    async deleteCustomer(customerId: string): Promise<void> {
        await deleteDoc(doc(customersRef, customerId));
        return;
    }
};
